import React from "react";
import { Link } from "gatsby";

const breakpoints = ["40em", "52em", "64em"];

export const media = {
  small: `screen and (min-width: ${breakpoints[0]})`,
  medium: `screen and (min-width: ${breakpoints[1]})`,
  large: `screen and (min-width: ${breakpoints[2]})`,
};

// Hover-intent grace period. Leaving the trigger or the panel starts this timer
// instead of closing immediately, so a pointer that clips a corner on its way
// down to the panel does not flap the menu shut. The 20px dead gap between the
// trigger and the panel (`.navLinkBase { top: calc(100% + 20px) }`) is bridged
// structurally by `.navLinkBase::after` in custom.scss; this delay is the
// belt-and-braces on top of it, not the mechanism.
const CLOSE_DELAY_MS = 150;

// Hover-intent on the way IN. Opening synchronously on mouseenter meant that
// merely sweeping the pointer across the header fired every dropdown it passed
// under: on a 696ms left-to-right sweep 4 of 21 sampled frames had both panels
// mounted. A pointer that is travelling somewhere else clears a 101px-wide
// trigger in well under this, so nothing opens; a pointer that stops to read
// one waits an imperceptible eighth of a second.
const OPEN_DELAY_MS = 120;

// Safety net for unmounting the panel. Closing is driven by the zoomOutDown
// animationend event; if the animation never runs (reduced motion, a browser
// that drops the keyframes, a backgrounded tab) the panel would stay mounted
// forever. It is inert while it is mounted-but-closed (`.nav-closed` ends on
// visibility: hidden with fill-mode forwards) but it should still come out of
// the DOM. Comfortably longer than the 0.15s animation.
const UNMOUNT_FALLBACK_MS = 400;

// ---------------------------------------------------------------------------
// Single-open registry.
//
// The dropdowns are sibling components with completely isolated state, and the
// panels are physically wider than the gap between their triggers (224px wide,
// triggers ~101px apart centre-to-centre — 118.8px of overlap at 1280px). So
// two panels open at once is not a cosmetic race, it is one panel painting on
// top of another and stealing its clicks. Timing alone cannot fix that: the
// outgoing panel's 150ms close grace period overlapped the incoming panel's
// open by design.
//
// Every mounted dropdown registers one stable entry here. Opening closes every
// other entry *synchronously and without animation* — a sibling being replaced
// gets no grace period and no fade, it is unmounted before the new panel is
// allowed to mount (see the ordering note in `openMenu`). The registry is a
// module-scoped Set rather than a context because it must survive nothing: it
// is written only in effects and cleaned up on unmount, so a page navigation
// that swaps the header cannot leave a stale closer behind.
// ---------------------------------------------------------------------------
const dropdownRegistry = new Set();

const closeOtherDropdowns = (self) => {
  dropdownRegistry.forEach((entry) => {
    if (entry !== self) entry.dismissNow();
  });
};

const normalizePath = (value) =>
  String(value || "")
    .split("?")[0]
    .split("#")[0]
    .replace(/\/+$/, "")
    .toLowerCase();

// Prefix match, not equality: /reviews/all/ has to keep "Reviews" marked and a
// city page under /areas-we-serve/ has to keep "Service Areas" marked. "/"
// normalises to "" and is therefore exact, otherwise Home would be active on
// every page on the site.
export const isPathActive = (pathname, url) => {
  const current = normalizePath(pathname);
  const target = normalizePath(url);
  if (!url) return false;
  if (target === "") return current === "";
  return current === target || current.startsWith(`${target}/`);
};

export const dropdownPanelId = (label) =>
  `rhi-nav-panel-${String(label || "menu")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")}`;

// True for any of the trigger's own url or its children's urls, so About reads
// active on /about/, /blog/ and /financing and Services on /roofing/, /siding/
// and /windows-doors/. A trigger is not a Gatsby Link's `activeClassName` case
// even now that it renders as one — its children's urls are what matter.
export const isDropdownActive = (pathname, item) => {
  if (!item) return false;
  if (isPathActive(pathname, item.url)) return true;
  const children = (item.submenu && item.submenu[0] && item.submenu[0].menuItems) || [];
  return children.some((child) => isPathActive(pathname, child && child.url));
};

function MenuItemDropdown({ item, label, source, headerTransparent, pathname }) {
  const name = label;
  // isOpen === the panel is MOUNTED (and therefore perceivable, and its links
  // are in the tab order). popupVisible === the panel is logically open; it
  // goes false at the start of the 0.15s close animation, while the panel is
  // still on screen. aria-expanded is reported from isOpen, not popupVisible,
  // so a screen reader is never told "collapsed" about something a sighted
  // user can still see and a keyboard user can still tab into.
  const [isOpen, setIsOpen] = React.useState(false);
  const [popupVisible, setPopupVisible] = React.useState(false);

  const wrapperRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const popupRef = React.useRef(null);
  const closeTimer = React.useRef(null);
  const openTimer = React.useRef(null);
  const registryEntry = React.useRef(null);

  const panelId = dropdownPanelId(name);
  const triggerUrl = item && item.url ? item.url : null;
  const isActive = isDropdownActive(pathname, item);

  const isSmallScreen = () => {
    return !window.matchMedia(media.small).matches;
  };

  // Touch screens report no hover. Without this gate a tap fires the browser's
  // synthesised mouseenter (-> open) and then the click (-> navigate), so the
  // panel would never get a chance to be read. Gating only the pointer
  // handlers keeps click/tap working on every device.
  const canHover = () => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  };

  const clearTimers = React.useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  }, []);

  // Hard close: no grace period, no close animation, out of the DOM in the
  // same synchronous pass. Used when a sibling dropdown is taking over, when a
  // click is about to navigate away, and when the pointer/focus has left the
  // header entirely. Built only from refs and React's own stable setters, so
  // the identity never changes and the registry entry can hold it forever.
  const dismissNow = React.useCallback(() => {
    clearTimers();
    setPopupVisible(false);
    setIsOpen(false);
  }, [clearTimers]);

  // Soft close: plays zoomOutDown, unmounts on animationend (or the fallback).
  const closeMenu = React.useCallback(() => {
    clearTimers();
    setPopupVisible(false);
  }, [clearTimers]);

  const openMenu = React.useCallback(() => {
    clearTimers();
    // Ordering is load-bearing. React 17 does not batch state updates from a
    // timeout callback, so this sibling close flushes its own re-render (and
    // unmounts the other panel) BEFORE the two setState calls below mount
    // ours. There is no commit, and therefore no paint, in which both panels
    // exist in the DOM.
    closeOtherDropdowns(registryEntry.current);
    setIsOpen(true);
    setPopupVisible(true);
  }, [clearTimers]);

  const openMenuSoon = React.useCallback(() => {
    clearTimers();
    openTimer.current = setTimeout(() => {
      openTimer.current = null;
      openMenu();
    }, OPEN_DELAY_MS);
  }, [clearTimers, openMenu]);

  const closeMenuSoon = React.useCallback(() => {
    clearTimers();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setPopupVisible(false);
    }, CLOSE_DELAY_MS);
  }, [clearTimers]);

  // Register exactly once per mounted dropdown. dismissNow is stable, so this
  // runs on mount/unmount only and cannot leak an entry across navigations.
  React.useEffect(() => {
    const entry = { dismissNow };
    registryEntry.current = entry;
    dropdownRegistry.add(entry);
    return () => {
      dropdownRegistry.delete(entry);
      registryEntry.current = null;
    };
  }, [dismissNow]);

  // ---- click -------------------------------------------------------------
  // Both triggers are real pages (About -> /about/, Services -> /services/),
  // and hover already exposes their children on a pointer device, so the
  // trigger is a link and a click NAVIGATES. The old behaviour — hover opens,
  // click closes — was the worst of both: the pointer was still inside the
  // wrapper afterwards so no fresh mouseenter could fire, and the menu stayed
  // shut until the user moved away and came back.
  //
  // Without hover there is nothing to expose the children, so the first tap
  // opens the panel instead of navigating; a second tap on the same trigger
  // follows the link, which is the only way the parent page stays reachable.
  const onTriggerClick = React.useCallback(
    (event) => {
      if (canHover()) {
        // Gatsby navigates client-side, so this component survives the route
        // change. Take the panel down in the same tick or it rides along.
        dismissNow();
        return;
      }
      if (!popupVisible) {
        event.preventDefault();
        openMenu();
      }
    },
    [popupVisible, dismissNow, openMenu]
  );

  // ---- pointer -----------------------------------------------------------
  // These sit on the group wrapper, which contains BOTH the trigger and the
  // absolutely-positioned panel, so mouseleave accounts for the panel too.
  const onPointerEnter = React.useCallback(() => {
    if (!canHover()) return;
    if (popupVisible) {
      // Already open — this is a re-entry from our own panel or the hover
      // bridge. Just cancel the pending close; do not re-arm the open delay.
      clearTimers();
      return;
    }
    openMenuSoon();
  }, [popupVisible, clearTimers, openMenuSoon]);

  const onPointerLeave = React.useCallback(() => {
    if (!canHover()) return;
    closeMenuSoon();
  }, [closeMenuSoon]);

  // ---- keyboard ----------------------------------------------------------
  const focusTrigger = React.useCallback(() => {
    if (triggerRef.current && typeof triggerRef.current.focus === "function") {
      triggerRef.current.focus();
    }
  }, []);

  // ArrowDown is the keyboard door into the children; the trigger itself is a
  // real link, so Enter navigates to the section page. Opening on focus was
  // tried and removed: Escape restores focus to the trigger, which re-fired
  // the focus handler and re-opened the panel it had just closed, and
  // Shift+Tabbing back through the bar popped both panels open over the page.
  const onTriggerKeyDown = React.useCallback(
    (event) => {
      if (event.key === "ArrowDown" || event.key === "Down") {
        event.preventDefault();
        openMenu();
        // The panel mounts in this same commit; focus the first link once it
        // is in the DOM.
        window.requestAnimationFrame(() => {
          const first = popupRef.current && popupRef.current.querySelector("a[href]");
          if (first) first.focus();
        });
      }
    },
    [openMenu]
  );

  // Tabbing out of the group closes it. Deliberately NOT closing on a focus
  // move that stays inside the wrapper.
  const onWrapperBlur = React.useCallback(
    (event) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      if (event.relatedTarget && wrapper.contains(event.relatedTarget)) return;
      closeMenu();
    },
    [closeMenu]
  );

  React.useEffect(() => {
    // crude implementation of animating the popup without a library
    const popupBox = popupRef.current;
    const onAnimationEnd = ({ animationName }) => {
      if (animationName === `zoomOutDown`) {
        setIsOpen(false);
      }
    };
    if (popupBox) {
      popupBox.addEventListener("animationend", onAnimationEnd);
      return () => {
        popupBox.removeEventListener("animationend", onAnimationEnd);
      };
    }
  }, [isOpen, name]);

  React.useEffect(() => {
    // Unmount the panel even if animationend never arrives. Re-opening
    // re-runs this effect, whose cleanup cancels the pending unmount.
    if (!isOpen || popupVisible) return undefined;
    const timer = setTimeout(() => setIsOpen(false), UNMOUNT_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [isOpen, popupVisible]);

  // Never leave a hover-open or hover-close timer running past unmount.
  React.useEffect(() => clearTimers, [clearTimers]);

  // WCAG 1.4.13: content revealed on hover must be dismissible without moving
  // the pointer. A hover-opened panel leaves focus on <body>, so a keydown
  // handler on the wrapper never fires — this has to be on the document. It
  // is attached only while the panel is mounted and removed the moment it is
  // not, so there is no permanent global listener.
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onDocumentKeyDown = (event) => {
      if (event.key !== "Escape" && event.key !== "Esc") return;
      const wrapper = wrapperRef.current;
      const focusWasInside = !!(wrapper && wrapper.contains(document.activeElement));
      closeMenu();
      if (focusWasInside) focusTrigger();
    };
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => {
      document.removeEventListener("keydown", onDocumentKeyDown);
    };
  }, [isOpen, closeMenu, focusTrigger]);

  React.useEffect(() => {
    // hide menu when clicked outside
    const handleClickOutside = (event) => {
      const wrapper = wrapperRef.current;
      if (!isSmallScreen() && isOpen && wrapper && !wrapper.contains(event.target)) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [name, isOpen, closeMenu]);

  const DropdownMenuItem = ({ item, type }) => {
    let itemId = null;
    let title = item.name;
    let slug = item.url;

    if (source && source == "mobilemenu") {
      return (
        <Link to={`${slug && slug.length < 2 ? "" : slug}`} className="block py-2 px-2 hover:underline font-normal">
          {item.name}
        </Link>
      );
    }
    // 44px rows (24px line + 2 x 10px), not the 64px of `px-6 py-5`: a three
    // item panel is now 148px instead of 192px of mostly air, while still
    // clearing the 44px pointer-target floor. Contrast raised from
    // neutral-700 to neutral-800 (12.6:1 on white), and the focus state is
    // explicit so keyboard users see where they are inside the panel.
    return (
      <Link
        to={`${slug}`}
        title={title ? title : item.name}
        onClick={dismissNow}
        className="block rounded-lg px-4 py-2.5 font-medium text-[15px] leading-6 text-neutral-800 hover:bg-neutral-100 hover:text-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-600 focus:bg-neutral-100 focus:text-brand-700 transition-colors duration-150 ease-in"
      >
        {item.name}
      </Link>
    );
  };

  if (source && source == "mobilemenu") {
    return (
      <div className="py-4 px-8 text-neutral-200 hover:text-white  block py-4 px-8 border-b border-white/20">
        <span className="font-semibold mb-2 block">{label}</span>
        {item.submenu[0].menuItems.map((item, i) => (
          <DropdownMenuItem key={item.id} item={item} type={item.type} />
        ))}
      </div>
    );
  }

  // Disclosure, not menu. These are ordinary navigation links, so the panel is
  // a plain container of links and the trigger owns aria-expanded +
  // aria-controls. role="menu" used to be here and was invalid on two counts:
  // its children were a bare <div> of <a> elements (axe aria-required-children)
  // and the arrow-key model a `menu` promises did not exist.
  const triggerClasses = `px-2 py-7 whitespace-nowrap border-transparent flex items-center transition-colors duration-150 ease-in rounded-lg font-semibold text-[15px] font-display text-base border-b-[3px]
        relative after:bg-brand-600 after:absolute after:h-[3px] after:w-0 after:bottom-[-3px] after:left-0 hover:after:w-full after:transition-all after:duration-300 cursor-pointer
        focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-brand-400
        ${isActive ? "rhi-nav-active" : ""}
        ${headerTransparent ? "text-white h  hover:text-brand-200" : "text-neutral-200 hover:text-white "}`;

  const triggerContent = (
    <>
      <span>{name}</span>
      <svg
        className={`ml-1 w-4 h-4  rotate-0  duration-75 transition-transform ease ${
          popupVisible ? "transform rotate-[180deg]" : ""
        }`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    </>
  );

  return (
    <div
      ref={wrapperRef}
      data-id={`${name}-group-wrapper`}
      onMouseEnter={onPointerEnter}
      onMouseLeave={onPointerLeave}
      onBlur={onWrapperBlur}
      className={`${
        headerTransparent ? "text-white hover:text-brand-200" : "text-neutral-800 hover:text-brand-600"
      } relative inline-block text-left font-normal text-base transition-colors duration-150 ease-in`}
      data-type={item.dropdownonesection}
    >
      <div>
        {triggerUrl ? (
          <Link
            ref={triggerRef}
            to={triggerUrl}
            data-id={`${name}-trigger`}
            aria-expanded={isOpen}
            aria-current={isActive ? "true" : undefined}
            aria-controls={isOpen ? panelId : undefined}
            onClick={onTriggerClick}
            onKeyDown={onTriggerKeyDown}
            className={triggerClasses}
          >
            {triggerContent}
          </Link>
        ) : (
          // No url on the CMS entry: fall back to a real button rather than a
          // link to nowhere. Same disclosure contract.
          <button
            ref={triggerRef}
            type="button"
            data-id={`${name}-trigger`}
            aria-expanded={isOpen}
            aria-current={isActive ? "true" : undefined}
            aria-controls={isOpen ? panelId : undefined}
            onClick={() => (popupVisible ? closeMenu() : openMenu())}
            onKeyDown={onTriggerKeyDown}
            className={triggerClasses}
          >
            {triggerContent}
          </button>
        )}
        {isOpen && (
          <div
            ref={popupRef}
            id={panelId}
            data-id={`${name}-popup-box`}
            className={`rhi-nav-dropdown bg-white rounded-xl shadow-xl ring-1 ring-black/5 p-2 navLinkBase ${
              popupVisible ? "nav-opened" : "nav-closed"
            }`}
          >
            <div>
              {item.submenu[0].menuItems.map((item, i) => (
                <DropdownMenuItem key={item.id} item={item} type={item.type} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MenuItemDropdown;
